// Gmail API client helpers for the GmailSearchPanel. Ports the
// legacy frontend's `gmail*` helpers (search / preview / fetch /
// decode / file conversion) to TS.
//
// Architecture: thin module of `fetch` wrappers. We hit the Gmail
// REST API directly with the operator's access token; nothing
// goes through the InRange backend. The output of `fetchThreadAsText`
// is a `File` the caller hands to the existing
// `/uploads/initiate` -> S3 PUT -> `/uploads/complete` flow, so
// imported threads end up alongside drag-dropped files as
// kind=email_thread.

export const GMAIL_MAX_RESULTS = 25;
const GMAIL_API_BASE = "https://gmail.googleapis.com/gmail/v1/users/me";

export interface GmailThreadStub {
  id: string;
  snippet: string;
}

export interface GmailThreadPreview {
  id: string;
  subject: string;
  from: string;
  date: string;
  snippet: string;
  messageCount: number;
}

interface GmailMessageHeader {
  name: string;
  value: string;
}

interface GmailMessagePart {
  mimeType?: string;
  body?: { data?: string };
  parts?: GmailMessagePart[];
}

interface GmailMessagePayload extends GmailMessagePart {
  headers?: GmailMessageHeader[];
}

interface GmailMessage {
  payload?: GmailMessagePayload;
  snippet?: string;
}

interface GmailThreadResponse {
  messages?: GmailMessage[];
}

interface GmailSearchResponse {
  threads?: { id: string; snippet?: string }[];
}

// ---------------------------------------------------------------------- //
// Search
// ---------------------------------------------------------------------- //

// Search threads matching the operator's Gmail query string. The
// query syntax mirrors Gmail's web UI - `from:`, `subject:`,
// `after:YYYY/MM/DD`, plain keywords, boolean OR. We list threads
// (not messages) so a multi-message conversation imports as one
// logical unit rather than N individually.
export const searchThreads = async (
  query: string,
  accessToken: string,
): Promise<GmailThreadStub[]> => {
  const url = new URL(`${GMAIL_API_BASE}/threads`);
  url.searchParams.set("q", query);
  url.searchParams.set("maxResults", String(GMAIL_MAX_RESULTS));
  const resp = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!resp.ok) {
    throw new Error(`Gmail search failed (${resp.status}): ${await resp.text()}`);
  }
  const data = (await resp.json()) as GmailSearchResponse;
  return (data.threads ?? []).map((t) => ({ id: t.id, snippet: t.snippet ?? "" }));
};

// ---------------------------------------------------------------------- //
// Preview - metadata-only fetch for the search results list
// ---------------------------------------------------------------------- //

// Lightweight: pull Subject/From/Date headers + first-message
// snippet without downloading the full body. The result list shows
// these; only PICKED threads pay the full-body fetch.
//
// NOTE: URLSearchParams collapses repeated keys into one which
// breaks `metadataHeaders=Subject&metadataHeaders=From` - build
// the URL manually.
export const getThreadPreview = async (
  threadId: string,
  accessToken: string,
): Promise<GmailThreadPreview> => {
  const built =
    `${GMAIL_API_BASE}/threads/${encodeURIComponent(threadId)}` +
    `?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=Date`;
  const resp = await fetch(built, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!resp.ok) {
    throw new Error(
      `Gmail thread preview failed (${resp.status}): ${await resp.text()}`,
    );
  }
  const data = (await resp.json()) as GmailThreadResponse;
  const firstMsg = data.messages?.[0] ?? {};
  const headers = headerMap(firstMsg.payload?.headers ?? []);
  return {
    id: threadId,
    subject: headers.subject ?? "(no subject)",
    from: headers.from ?? "",
    date: headers.date ?? "",
    snippet: firstMsg.snippet ?? "",
    messageCount: data.messages?.length ?? 1,
  };
};

// ---------------------------------------------------------------------- //
// Full thread -> text/plain
// ---------------------------------------------------------------------- //

// Fetch the full thread + render to a single text/plain document
// the agent can read via `extract_text`. Per-message headers
// (Subject / From / To / Cc / Date) are prepended so the agent has
// the metadata alongside the body.
//
// Returns `{ text, subject }`; the subject is the FIRST message's
// subject, used as the filename basis when the caller wraps the
// text in a `File`.
export const fetchThreadAsText = async (
  threadId: string,
  accessToken: string,
): Promise<{ text: string; subject: string }> => {
  const url = `${GMAIL_API_BASE}/threads/${encodeURIComponent(threadId)}?format=full`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!resp.ok) {
    throw new Error(`Gmail thread fetch failed (${resp.status}): ${await resp.text()}`);
  }
  const data = (await resp.json()) as GmailThreadResponse;
  const parts: string[] = [];
  for (const msg of data.messages ?? []) {
    const headers = headerMap(msg.payload?.headers ?? []);
    parts.push(
      "--- Message ---",
      `Subject: ${headers.subject ?? ""}`,
      `From: ${headers.from ?? ""}`,
      `To: ${headers.to ?? ""}`,
      `Cc: ${headers.cc ?? ""}`,
      `Date: ${headers.date ?? ""}`,
      "",
      decodeBody(msg.payload),
      "",
    );
  }
  const text = parts.join("\n");
  const firstHeaders = headerMap(data.messages?.[0]?.payload?.headers ?? []);
  const subject = firstHeaders.subject ?? `Thread ${threadId}`;
  return { text, subject };
};

// ---------------------------------------------------------------------- //
// Conversion: thread text -> File for the upload pipeline
// ---------------------------------------------------------------------- //

// Slugify the subject and append a short thread-id suffix so the
// filename is unique per thread + safe for path normalisation.
// The MIME type is `text/plain` so the FE `inferKind` classifier
// tags the file as `email_thread` and the backend's
// `extract_text` tool can read it.
export const threadToFile = (
  text: string,
  subject: string,
  threadId: string,
): File => {
  const slug =
    (subject ?? "email")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "email";
  const name = `${slug}-${threadId.slice(0, 8)}.txt`;
  const blob = new Blob([text], { type: "text/plain" });
  return new File([blob], name, { type: "text/plain" });
};

// ---------------------------------------------------------------------- //
// Internal: header + body decoding
// ---------------------------------------------------------------------- //

const headerMap = (headers: GmailMessageHeader[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const h of headers) {
    out[h.name.toLowerCase()] = h.value;
  }
  return out;
};

// Walk the MIME tree, return the first text/plain body; fall back
// to text/html with tags stripped. Multi-part conversations land
// here for every message in the thread.
const decodeBody = (payload: GmailMessagePart | undefined): string => {
  if (!payload) return "";
  const found: { kind: "plain" | "html"; text: string }[] = [];
  const walk = (node: GmailMessagePart | undefined): void => {
    if (!node) return;
    const mime = node.mimeType ?? "";
    if (mime === "text/plain" && node.body?.data) {
      found.push({ kind: "plain", text: b64UrlDecode(node.body.data) });
    } else if (mime === "text/html" && node.body?.data) {
      found.push({ kind: "html", text: b64UrlDecode(node.body.data) });
    }
    for (const child of node.parts ?? []) walk(child);
  };
  walk(payload);
  const plain = found.find((f) => f.kind === "plain");
  if (plain) return plain.text;
  const html = found.find((f) => f.kind === "html");
  if (html) {
    return html.text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  return "";
};

// Gmail returns base64url-encoded body data. Convert to standard
// base64 then to a UTF-8 string. `atob` handles latin-1; round-
// trip through a Uint8Array + TextDecoder for unicode safety.
const b64UrlDecode = (s: string): string => {
  const standard = s.replace(/-/g, "+").replace(/_/g, "/");
  const padded = standard.padEnd(
    standard.length + ((4 - (standard.length % 4)) % 4),
    "=",
  );
  try {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  } catch {
    return "";
  }
};
